import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  Boxes,
  Camera,
  Factory,
  ImageOff,
  MapPin,
  MonitorSmartphone,
  Package,
  ShieldAlert,
  Timer,
  Users,
  Wrench,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { EmptyState } from '@/components/ui/EmptyState';
import { StoredImage, WorkerChip } from '@/components/production/shared';
import { StationOverviewTab } from '@/components/production/station/StationOverviewTab';
import { StationPhotosTab } from '@/components/production/station/StationPhotosTab';
import { StationToolsTab } from '@/components/production/station/StationToolsTab';
import { StationPartsTab } from '@/components/production/station/StationPartsTab';
import { StationInstructionsTab } from '@/components/production/station/StationInstructionsTab';
import { StationPeopleTab } from '@/components/production/station/StationPeopleTab';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { useOpenSessions, useWorkInstructions, useWorkRealtime } from '@/features/workMode/hooks';
import {
  useStationItemIds,
  useStationParts,
  useStationPhotos,
  useStationTools,
} from '@/features/workMode/stationSetup';
import { useWorkstationSignoffs } from '@/features/workMode/signoffs';
import { useWorkLookups } from '@/features/workMode/useLookups';
import { formatDuration } from '@/features/workMode/api';

const TABS = ['overview', 'photos', 'tools', 'parts', 'instructions', 'people'] as const;
type Tab = (typeof TABS)[number];

/**
 * Manufacturing → Workstations → one station. Everything that describes
 * the bench: the basics, photos, tools, parts, the work instructions that
 * apply and who is signed off. What managers set here is what the tablet
 * shows under "Station" in Work Mode.
 */
export function WorkstationDetailPage() {
  const { stationId = '' } = useParams();
  const { role } = useAuth();
  const canManage = canWrite(role, 'manufacturing');
  const canRename = role === 'super_admin' || role === 'admin';
  const lookups = useWorkLookups();
  const [params, setParams] = useSearchParams();
  const tab: Tab = (TABS as readonly string[]).includes(params.get('tab') ?? '')
    ? (params.get('tab') as Tab)
    : 'overview';

  useWorkRealtime(true);

  const station = lookups.stations.get(stationId) ?? null;
  const { data: photos = [] } = useStationPhotos(station?.id ?? null);
  const { data: tools = [] } = useStationTools(station?.id ?? null);
  const { data: parts = [] } = useStationParts(station?.id ?? null);
  const { data: linkedItemIds = [] } = useStationItemIds(station?.id ?? null);
  const { data: sets = [] } = useWorkInstructions();
  const { data: signoffs = [] } = useWorkstationSignoffs();
  const { data: sessions = [] } = useOpenSessions();

  const liveSets = useMemo(() => {
    if (!station) return 0;
    const itemIds = new Set(linkedItemIds);
    if (station.default_item_id) itemIds.add(station.default_item_id);
    const pinned = sets.filter((s) => s.workstation_id === station.id && s.is_published);
    const pinnedItems = new Set(pinned.map((s) => s.item_id));
    const generic = sets.filter(
      (s) =>
        s.workstation_id === null &&
        s.is_published &&
        itemIds.has(s.item_id) &&
        !pinnedItems.has(s.item_id),
    );
    return pinned.length + generic.length;
  }, [sets, station, linkedItemIds]);

  const people = useMemo(
    () =>
      signoffs.filter(
        (s) =>
          s.workstation_id === stationId && !s.revoked_at && lookups.person(s.user_id).isActive,
      ).length,
    [signoffs, stationId, lookups],
  );
  const session = useMemo(
    () => sessions.find((s) => s.workstation_id === stationId) ?? null,
    [sessions, stationId],
  );

  const setTab = (next: string) => {
    const p = new URLSearchParams(params);
    if (next === 'overview') p.delete('tab');
    else p.set('tab', next);
    setParams(p, { replace: true });
  };

  if (lookups.loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 rounded-2xl" />
        <Skeleton className="h-80 rounded-2xl" />
      </div>
    );
  }

  if (!station) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Manufacturing / Workstations" title="Station not found" />
        <EmptyState
          icon={Factory}
          title="This station no longer exists."
          action={
            <Button asChild variant="outline">
              <Link to="/mfg/workstations">
                <ArrowLeft className="h-4 w-4" />
                Back to Workstations
              </Link>
            </Button>
          }
        />
      </div>
    );
  }

  const worker = session ? lookups.person(session.user_id) : null;
  const defaultItem = station.default_item_id ? lookups.itemName(station.default_item_id) : null;
  const buildsCount = new Set([
    ...linkedItemIds,
    ...(station.default_item_id ? [station.default_item_id] : []),
  ]).size;
  const requiredTools = tools.filter((t) => t.is_required).length;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={
          <Link to="/mfg/workstations" className="hover:underline">
            Manufacturing / Workstations
          </Link>
        }
        title={station.name}
        meta={station.description || (defaultItem ? `Builds ${defaultItem}` : 'Station setup')}
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline">
              <Link to="/digital-company?arrange=1">
                <MapPin className="h-4 w-4" />
                On the map
              </Link>
            </Button>
            <Button asChild>
              <Link to="/work">
                <MonitorSmartphone className="h-4 w-4" />
                Open Work Mode
              </Link>
            </Button>
          </div>
        }
      />

      <section className="grid gap-4 overflow-hidden rounded-2xl border border-border bg-card lg:grid-cols-[320px_minmax(0,1fr)]">
        <button
          type="button"
          onClick={() => setTab('photos')}
          className="group relative aspect-[4/3] bg-secondary text-left lg:aspect-auto lg:min-h-[220px]"
          aria-label="Open photos"
        >
          {station.cover_image_path ? (
            <StoredImage
              bucket="work-instructions"
              path={station.cover_image_path}
              alt={station.name}
              className="h-full w-full rounded-none"
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
              <ImageOff className="h-8 w-8" />
              <span className="text-xs">
                {canManage ? 'Add a photo of the bench' : 'No photo yet'}
              </span>
            </div>
          )}
          {photos.length > 0 && (
            <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-humus/80 px-2 py-0.5 text-[11px] font-medium text-white">
              <Camera className="h-3 w-3" />
              {photos.length}
            </span>
          )}
        </button>

        <div className="flex flex-col gap-4 p-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={station.is_active ? 'success-soft' : 'muted'}>
              {station.is_active ? 'Active' : 'Switched off'}
            </Badge>
            {session && worker ? (
              <Badge variant="info-soft">Working now</Badge>
            ) : station.is_active ? (
              <Badge variant="outline">Empty</Badge>
            ) : null}
            {station.floor_x == null && <Badge variant="outline">Not on the map</Badge>}
            {station.ppe.map((p) => (
              <Badge key={p} variant="warning-soft">
                {p}
              </Badge>
            ))}
          </div>

          {session && worker && (
            <WorkerChip
              person={worker}
              sub={`Building ${lookups.itemName(session.item_id) ?? 'nothing in particular'} · ${session.units_completed} this shift`}
              size={36}
            />
          )}

          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3 xl:grid-cols-6">
            <Fact
              icon={Package}
              label="Builds"
              value={buildsCount ? `${buildsCount} item${buildsCount === 1 ? '' : 's'}` : '—'}
              hint={defaultItem ?? undefined}
              onClick={() => setTab('overview')}
            />
            <Fact
              icon={Timer}
              label="Target"
              value={station.target_seconds ? formatDuration(station.target_seconds) : '—'}
              hint="per unit"
              onClick={() => setTab('overview')}
            />
            <Fact
              icon={Wrench}
              label="Tools"
              value={String(tools.length)}
              hint={tools.length ? `${requiredTools} required` : undefined}
              onClick={() => setTab('tools')}
            />
            <Fact
              icon={Boxes}
              label="Parts"
              value={String(parts.length)}
              hint={parts.length ? 'stocked here' : undefined}
              onClick={() => setTab('parts')}
            />
            <Fact
              icon={BookOpen}
              label="Instructions"
              value={String(liveSets)}
              hint={liveSets ? 'live on the tablet' : 'none live'}
              tone={liveSets === 0 && buildsCount > 0 ? 'warn' : undefined}
              onClick={() => setTab('instructions')}
            />
            <Fact
              icon={Users}
              label="Signed off"
              value={String(people)}
              hint={people ? 'people' : 'nobody yet'}
              onClick={() => setTab('people')}
            />
          </dl>

          {(station.location_notes || station.safety_notes) && (
            <div className="grid gap-2 text-sm sm:grid-cols-2">
              {station.location_notes && (
                <p className="flex items-start gap-2 text-muted-foreground">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{station.location_notes}</span>
                </p>
              )}
              {station.safety_notes && (
                <p className="flex items-start gap-2 text-fresh-salmon-dark">
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="line-clamp-2">{station.safety_notes}</span>
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto flex-wrap justify-start">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="photos">
            Photos <Count n={photos.length} />
          </TabsTrigger>
          <TabsTrigger value="tools">
            Tools <Count n={tools.length} />
          </TabsTrigger>
          <TabsTrigger value="parts">
            Parts <Count n={parts.length} />
          </TabsTrigger>
          <TabsTrigger value="instructions">
            Instructions <Count n={liveSets} />
          </TabsTrigger>
          <TabsTrigger value="people">
            People <Count n={people} />
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <StationOverviewTab
            key={station.id}
            station={station}
            lookups={lookups}
            canManage={canManage}
            canRename={canRename}
          />
        </TabsContent>
        <TabsContent value="photos">
          <StationPhotosTab station={station} canManage={canManage} />
        </TabsContent>
        <TabsContent value="tools">
          <StationToolsTab station={station} canManage={canManage} />
        </TabsContent>
        <TabsContent value="parts">
          <StationPartsTab station={station} lookups={lookups} canManage={canManage} />
        </TabsContent>
        <TabsContent value="instructions">
          <StationInstructionsTab station={station} lookups={lookups} canManage={canManage} />
        </TabsContent>
        <TabsContent value="people">
          <StationPeopleTab station={station} lookups={lookups} canManage={canManage} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Fact({
  icon: Icon,
  label,
  value,
  hint,
  tone,
  onClick,
}: {
  icon: typeof Package;
  label: string;
  value: string;
  hint?: string;
  tone?: 'warn';
  onClick: () => void;
}) {
  return (
    <div>
      <dt className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
      </dt>
      <dd>
        <button type="button" onClick={onClick} className="text-left hover:underline">
          <span
            className={cn(
              'text-lg font-medium tabular-nums',
              tone === 'warn' ? 'text-fresh-salmon-dark' : 'text-foreground',
            )}
          >
            {value}
          </span>
          {hint && <span className="block truncate text-xs text-muted-foreground">{hint}</span>}
        </button>
      </dd>
    </div>
  );
}

function Count({ n }: { n: number }) {
  if (n <= 0) return null;
  return (
    <span className="ml-1.5 rounded-full bg-secondary px-1.5 text-[10px] font-medium tabular-nums text-muted-foreground">
      {n}
    </span>
  );
}
